import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import type { PaginatedResult } from '@tms/shared';

import axios from 'axios';

import { api } from './client';
import { errorNotice } from './errorNotice';

export interface Dictionaries {
  fuelTypes: Array<{ id: number; code: string; name: string; density: string }>;
  vehicleModels: Array<{
    id: number;
    category: string;
    manufacturer: string;
    model: string;
    meterType: string;
    tankCapacity: string | null;
    fuelTypeId: number | null;
    seats: number | null;
  }>;
  departments: Array<{ id: number; code: string; name: string; parentId: number | null }>;
  positions: Array<{
    id: number;
    departmentId: number;
    code: string;
    name: string;
    requiresDriverLicense: boolean;
  }>;
  permitTypes: Array<{ id: number; name: string }>;
  documentTypes: Array<{ id: number; name: string }>;
  counterparties: Array<{ id: number; name: string; inn: string | null }>;
  violationTypes: Array<{ id: number; code: string; name: string; defaultFineAmount: string | null }>;
  taskLocations: Array<{ id: number; code: string; name: string }>;
  regions: Array<{
    id: number;
    code: string;
    name: string;
    districts: Array<{ id: number; code: string; name: string }>;
  }>;
}

/**
 * Справочники для форм. Кэшируются надолго: номенклатура техники и видов
 * топлива меняется в лучшем случае раз в квартал, а нужна почти в каждой форме.
 */
export function useDictionaries() {
  return useQuery({
    queryKey: ['dictionaries'],
    staleTime: 30 * 60_000,
    queryFn: async () => {
      const { data } = await api.get<Dictionaries>('/dictionaries');
      return data;
    },
  });
}

/**
 * Значение с задержкой — то, что реально уходит в запрос на сервер.
 *
 * Без неё поле поиска слало бы отдельный запрос на каждую нажатую букву:
 * пока пользователь печатает «Юсупов», сервер получил бы «Ю», «Юс», «Юсу»
 * и так далее. Задержка гасит промежуточные значения и в запрос попадает
 * только то, на чём пользователь остановился — при этом поле уже реагирует
 * на каждое нажатие, ждать полного слова или нажатия Enter не нужно.
 */
export function useDebouncedValue<T>(value: T, delayMs = 300): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);

  return debounced;
}

/** Списочный запрос с пагинацией. */
export function usePaged<T>(
  key: unknown[],
  url: string,
  params: Record<string, unknown>,
  enabled = true,
) {
  return useQuery({
    queryKey: [...key, params],
    enabled,
    queryFn: async () => {
      const { data } = await api.get<PaginatedResult<T>>(url, { params });
      return data;
    },
  });
}

/** Сколько раз подряд ждём, если API просит снизить частоту запросов. */
const RATE_LIMIT_RETRIES = 20;
/**
 * Размер пачки для отчёта — наибольший, который отдаёт API (см. MAX_PAGE_SIZE
 * в pagination.dto.ts). Журнал в 30 000 строк — это 15 обращений вместо 150.
 */
const EXPORT_PAGE_SIZE = 2000;
/** Наибольшая страница у API до увеличения границы. */
const LEGACY_PAGE_SIZE = 200;

/**
 * Все строки постраничного списка — для отчёта «весь список».
 *
 * Экран показывает одну страницу, а отчёт нужен по всему, что подходит под
 * фильтр. Предела по числу строк нет: за месяц журнал набирает тысячи
 * записей, а отчёт берут и за несколько месяцев сразу.
 *
 * Строки забираются пачками по очереди: параллельные запросы упёрлись бы в
 * ограничение частоты. Если в него всё же упереться, запрос повторяется
 * после паузы, а не обрывает выгрузку на середине.
 */
export async function fetchAllPages<T>(
  url: string,
  params: Record<string, unknown>,
  onProgress?: (loaded: number, total: number) => void,
): Promise<T[]> {
  let pageSize = EXPORT_PAGE_SIZE;
  let first: PaginatedResult<T>;

  try {
    first = await fetchPage<T>(url, { ...params, page: 1, pageSize });
  } catch (error) {
    // Сервер прежней версии принимает страницу не больше 200 строк и на
    // крупную отвечает ошибкой проверки. Сайт и API выкатываются по
    // отдельности, поэтому со старым API отчёт не должен ломаться —
    // он просто соберётся мелкими пачками.
    const rejected = axios.isAxiosError(error) && error.response?.status === 400;
    if (!rejected) throw error;
    pageSize = LEGACY_PAGE_SIZE;
    first = await fetchPage<T>(url, { ...params, page: 1, pageSize });
  }

  const rows: T[] = [...first.items];
  let total = first.meta.total;
  onProgress?.(rows.length, total);

  for (let page = 2; rows.length < total; page += 1) {
    const data = await fetchPage<T>(url, { ...params, page, pageSize });
    if (data.items.length === 0) break;
    rows.push(...data.items);
    total = data.meta.total;
    onProgress?.(rows.length, total);
  }

  return rows;
}

async function fetchPage<T>(url: string, params: Record<string, unknown>): Promise<PaginatedResult<T>> {
  for (let attempt = 0; ; attempt += 1) {
    try {
      return (await api.get<PaginatedResult<T>>(url, { params })).data;
    } catch (error) {
      const limited = axios.isAxiosError(error) && error.response?.status === 429;
      if (!limited || attempt >= RATE_LIMIT_RETRIES) throw error;

      // Сервер сам говорит, сколько ждать; без подсказки — пять секунд.
      const retryAfter = Number(error.response?.headers['retry-after']);
      const seconds = Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : 5;
      await new Promise((resolve) => setTimeout(resolve, seconds * 1000));
    }
  }
}

/**
 * Мутация с уведомлением и обновлением связанных списков.
 *
 * Сообщения об ошибках берутся из поля message ответа API: сервер отдаёт
 * осмысленный текст («В ёмкости недостаточно топлива: остаток 120 л»),
 * и подменять его общей фразой «Ошибка сохранения» — терять смысл.
 */
export function useApiMutation<TVariables, TResult = unknown>(
  request: (variables: TVariables) => Promise<TResult>,
  options: { successMessage?: string; invalidate?: unknown[][] } = {},
) {
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: request,
    onSuccess: () => {
      if (options.successMessage) void message.success(options.successMessage);
      for (const key of options.invalidate ?? []) {
        void queryClient.invalidateQueries({ queryKey: key });
      }
    },
    onError: (error) => {
      void message.error(errorNotice(error));
    },
  });
}

/**
 * Загружает картинку из защищённого эндпоинта и отдаёт blob-ссылку.
 *
 * Обычный `<img src="/api/...">` не подходит: браузер не приложит к такому
 * запросу заголовок Authorization, и сервер ответит 401. Ссылка живёт до
 * размонтирования компонента, потом освобождается.
 */
export function useAuthedImage(url: string | null): string | null {
  const [href, setHref] = useState<string | null>(null);

  useEffect(() => {
    if (!url) {
      setHref(null);
      return;
    }

    let objectUrl: string | null = null;
    let cancelled = false;

    void api
      .get(url, { responseType: 'blob' })
      .then((response) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(response.data as Blob);
        setHref(objectUrl);
      })
      .catch(() => setHref(null));

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url]);

  return href;
}

/**
 * Скачивание файла из защищённого эндпоинта.
 *
 * Обычная ссылка не подходит: в заголовке нужен токен, а положить его
 * в адресную строку — значит оставить в истории браузера и логах прокси.
 */
export function useDownload() {
  const { message } = App.useApp();

  return useCallback(
    async (url: string, params: Record<string, unknown>, fallbackName: string) => {
      try {
        const response = await api.get(url, { params, responseType: 'blob' });

        const disposition = String(response.headers['content-disposition'] ?? '');
        const match = /filename="?([^"]+)"?/.exec(disposition);
        const fileName = match ? match[1] : fallbackName;

        const href = URL.createObjectURL(response.data as Blob);
        const link = document.createElement('a');
        link.href = href;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(href);
      } catch (error) {
        void message.error(errorNotice(error));
      }
    },
    [message],
  );
}
